import { useIsEmailExistMutation } from '@ports/internal-services/user/userSlice';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type UserEmailExistResult = {
  userId: string;
  name: string;
  profileImage: string | null;
};

export const useUserEmailExistCheck = (props?: { formId?: string }) => {
  const { t } = useTranslation();
  const [isEmailExist] = useIsEmailExistMutation();
  const [emailError, setEmailError] = useState<string>('');
  const [existingUser, setExistingUser] = useState<UserEmailExistResult | null>(
    null
  );

  // Focus email input when emailError is set
  useEffect(() => {
    if (props?.formId) {
      const emailInputRef = document.querySelector(
        `#${props?.formId} input[name="email"]`
      ) as HTMLInputElement;
      if (emailError && emailInputRef) {
        emailInputRef.focus();
      }
    }
  }, [emailError, props?.formId]);

  useEffect(() => {
    if (!emailError) {
      setExistingUser(null); // Clear existing user info when there's no email error
    }
  }, [emailError]);

  const handleEmailExistCheck = useCallback(
    async (email: string): Promise<boolean> => {
      setEmailError('');
      if (!email) {
        return true; // If email is empty, we consider it as valid (no need to check existence)
      }
      try {
        const res = await isEmailExist({
          isEmailExist: {
            email: email
          }
        }).unwrap();

        if (res?.data) {
          setEmailError(t(`serverMessages.${res.message}`, res?.data));
          if (res.message !== 'PEOPLE-105') {
            const userData = res.data as any;
            setExistingUser({
              userId: userData?.userId,
              name: `${userData?.userDetail?.firstName || ''} ${userData?.userDetail?.lastName || ''}`.trim(),
              profileImage: userData?.userDetail?.profileImage
            });
          }
          return false;
        } else {
          setEmailError('');
        }
      } catch (err: any) {
        if (err?.status === 404) {
          // Email does not exist, which is expected, so we can proceed
          setEmailError('');
        } else {
          // An actual error occurred while checking email existence
          console.error('Error checking email existence:', err);
          setEmailError(t('serverMessages.SERVER-ERROR'));
          return false;
        }
      }
      return true;
    },
    []
  );
  return {
    handleEmailExistCheck,
    emailError,
    setEmailError,
    existingUser,
    setExistingUser
  };
};
